import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { toast, ToastContainer } from "react-toastify";

import Loader from "../Loader";
import {
  deleteBlog,
  fetchBlogs,
  clearBlogErrors,
} from "../../features/blog/blogSlice";
import { formatDate } from "../../utils";
import "react-toastify/dist/ReactToastify.css";

const stripHtml = (html) =>
  DOMPurify.sanitize(html || "", { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
    .replace(/&nbsp;/g, " ")
    .trim();

const BlogsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, status, error, deleteStatus, deleteError } = useSelector(
    (state) => state.blog
  );

  const [search] = useState("");

  useEffect(() => {
    // Only fetch if not already loading
    if (status === "idle") {
      dispatch(fetchBlogs());
    }
  }, [dispatch, status]);

  useEffect(() => {
    if (deleteStatus === "succeeded") {
      toast.success("Blog deleted successfully.");
    } else if (deleteStatus === "failed" && deleteError) {
      toast.error(deleteError);
    }
  }, [deleteStatus, deleteError]);

  useEffect(() => {
    return () => {
      dispatch(clearBlogErrors());
    };
  }, [dispatch]);

  const filteredBlogs = useMemo(() => {
    if (!search.trim()) return items;
    const lowered = search.toLowerCase();
    return items.filter((blog) =>
      (blog?.title || "").toLowerCase().includes(lowered)
    );
  }, [items, search]);

  const handleDelete = async (blogId) => {
    if (!blogId) {
      toast.error("Blog id not found.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this blog?"
    );

    if (!confirmDelete) return;

    try {
      await dispatch(deleteBlog(blogId)).unwrap();
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : err?.message || "Failed to delete blog."
      );
    }
  };

  const handleRefresh = () => {
    dispatch(fetchBlogs());
  };

  const handleEdit = (blog) => {
    navigate("/edit-blog", { state: { blog } });
  };

  if (status === "loading") {
    return (
      <div className="card h-100 radius-12">
        <div className="card-body d-flex align-items-center justify-content-center">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 radius-12">
      <div className="card-header bg-base border-bottom py-16 px-24 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <h5 className="mb-0">All Blogs</h5>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
            onClick={handleRefresh}
          >
            <Icon icon="mdi:refresh" className="text-lg" />
            Refresh
          </button>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* <div className="navbar-search">
            <input
              type="text"
              name="search"
              className="bg-base h-40-px"
              placeholder="Search by title"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </div> */}
          <Link
            to="/add-blog"
            className="btn btn-bg btn-sm d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="text-lg" />
            Add Blog
          </Link>
        </div>
      </div>

      <div className="card-body p-24">
        {status === "failed" && error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {filteredBlogs.length === 0 ? (
          <div className="text-center py-40">
            <Icon
              icon="mdi:post-outline"
              className="text-4xl mb-12 text-secondary-light opacity-75"
            />
            <p className="mb-0 text-secondary-light">
              {search.trim()
                ? "No blogs match your search."
                : "No blogs have been created yet."}
            </p>
          </div>
        ) : (
          <div className="table-responsive scroll-sm">
            <table className="table mb-0 bordered-table sm-table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Title</th>
                  <th scope="col">Description</th>
                  <th scope="col">Image</th>
                  <th scope="col">Created</th>
                  <th scope="col" className="text-end">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBlogs.map((blog, index) => {
                  const blogId = blog?._id || blog?.id || blog?.uuid || index;
                  return (
                    <tr key={blogId}>
                      <td>{index + 1}</td>
                      <td className="fw-semibold text-secondary-light">
                        {blog?.title || "Untitled"}
                      </td>
                      <td className="text-secondary-light">
                        {stripHtml(blog?.description).slice(0, 120) ||
                          "No description"}
                        {stripHtml(blog?.description).length > 120 && "..."}
                      </td>
                      <td>
                        {blog?.img ? (
                          <img
                            src={blog.img}
                            alt={blog?.title || "Blog image"}
                            className="w-80-px h-80-px object-fit-cover rounded-3"
                          />
                        ) : (
                          <span className="badge bg-neutral-200 text-neutral-600">
                            No image
                          </span>
                        )}
                      </td>
                      <td>
                        {blog?.createdAt
                          ? formatDate(blog.createdAt)
                          : "—"}
                      </td>
                      <td className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                            onClick={() => handleEdit(blog)}
                          >
                            <Icon icon="mingcute:edit-line" className="text-lg" />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                            onClick={() => handleDelete(blog?._id || blog?.id)}
                            disabled={deleteStatus === "loading"}
                          >
                            <Icon icon="mingcute:delete-line" className="text-lg" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default BlogsList;

// import { useEffect, useMemo, useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { Icon } from "@iconify/react/dist/iconify.js";
// import { Link, useNavigate } from "react-router-dom";
// import DOMPurify from "dompurify";
// import { toast, ToastContainer } from "react-toastify";

// import Loader from "../Loader";
// import {
//   deleteBlog,
//   fetchBlogs,
//   clearBlogErrors,
// } from "../../features/blog/blogSlice";
// import { formatDate } from "../../utils";
// import "react-toastify/dist/ReactToastify.css";

// const stripHtml = (html) =>
//   DOMPurify.sanitize(html || "", { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
//     .replace(/&nbsp;/g, " ")
//     .trim();

// const BlogsList = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { items, status, error, deleteStatus, deleteError } = useSelector(
//     (state) => state.blog
//   );

//   const [search, setSearch] = useState("");

//   useEffect(() => {
//     // Only fetch if not already loading
//     if (status === "idle") {
//       dispatch(fetchBlogs());
//     }
//   }, [dispatch]); // Remove status from dependencies to prevent loops

//   useEffect(() => {
//     if (deleteStatus === "succeeded") {
//       toast.success("Blog deleted successfully.");
//     } else if (deleteStatus === "failed" && deleteError) {
//       toast.error(deleteError);
//     }
//   }, [deleteStatus, deleteError]);

//   useEffect(() => {
//     return () => {
//       dispatch(clearBlogErrors());
//     };
//   }, [dispatch]);

//   const filteredBlogs = useMemo(() => {
//     if (!search.trim()) return items;
//     const lowered = search.toLowerCase();
//     return items.filter((blog) =>
//       (blog?.title || "").toLowerCase().includes(lowered)
//     );
//   }, [items, search]);

//   const handleDelete = async (blogId) => {
//     if (!blogId) {
//       toast.error("Blog id not found.");
//       return;
//     }

//     const confirmDelete = window.confirm(
//       "Are you sure you want to delete this blog?"
//     );

//     if (!confirmDelete) return;

//     try {
//       await dispatch(deleteBlog(blogId)).unwrap();
//     } catch (err) {
//       toast.error(
//         typeof err === "string" ? err : err?.message || "Failed to delete blog."
//       );
//     }
//   };

//   const handleRefresh = () => {
//     dispatch(fetchBlogs());
//   };

//   const handleEdit = (blog) => {
//     navigate("/edit-blog", { state: { blog } });
//   };

//   if (status === "loading") {
//     return (
//       <div className="card h-100 radius-12">
//         <div className="card-body d-flex align-items-center justify-content-center">
//           <Loader />
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="card h-100 radius-12">
//       <div className="card-header bg-base border-bottom py-16 px-24 d-flex flex-wrap align-items-center justify-content-between gap-3">
//         <div className="d-flex flex-wrap align-items-center gap-3">
//           <h5 className="mb-0">All Blogs</h5>
//           <button
//             type="button"
//             className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
//             onClick={handleRefresh}
//           >
//             <Icon icon="mdi:refresh" className="text-lg" />
//             Refresh
//           </button>
//         </div>
//         <div className="d-flex flex-wrap align-items-center gap-3">
//           {/* <div className="navbar-search">
//             <input
//               type="text"
//               name="search"
//               className="bg-base h-40-px"
//               placeholder="Search by title"
//               value={search}
//               onChange={(event) => setSearch(event.target.value)}
//             />
//             <Icon icon="ion:search-outline" className="icon" />
//           </div> */}
//           <Link
//             to="/add-blog"
//             className="btn btn-bg btn-sm d-flex align-items-center gap-2"
//           >
//             <Icon icon="ic:baseline-plus" className="text-lg" />
//             Add Blog
//           </Link>
//         </div>
//       </div>

//       <div className="card-body p-24">
//         {status === "failed" && error && (
//           <div className="alert alert-danger" role="alert">
//             {error}
//           </div>
//         )}

//         {filteredBlogs.length === 0 ? (
//           <div className="text-center py-40">
//             <Icon
//               icon="mdi:post-outline"
//               className="text-4xl mb-12 text-secondary-light opacity-75"
//             />
//             <p className="mb-0 text-secondary-light">
//               {search.trim()
//                 ? "No blogs match your search."
//                 : "No blogs have been created yet."}
//             </p>
//           </div>
//         ) : (
//           <div className="table-responsive scroll-sm">
//             <table className="table mb-0 bordered-table sm-table">
//               <thead>
//                 <tr>
//                   <th scope="col">#</th>
//                   <th scope="col">Title</th>
//                   <th scope="col">Description</th>
//                   <th scope="col">Image</th>
//                   <th scope="col">Created</th>
//                   <th scope="col" className="text-end">
//                     Actions
//                   </th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {filteredBlogs.map((blog, index) => {
//                   const blogId = blog?._id || blog?.id || blog?.uuid || index;
//                   return (
//                     <tr key={blogId}>
//                       <td>{index + 1}</td>
//                       <td className="fw-semibold text-secondary-light">
//                         {blog?.title || "Untitled"}
//                       </td>
//                       <td className="text-secondary-light">
//                         {stripHtml(blog?.description).slice(0, 120) ||
//                           "No description"}
//                         {stripHtml(blog?.description).length > 120 && "..."}
//                       </td>
//                       <td>
//                         {blog?.img ? (
//                           <img
//                             src={blog.img}
//                             alt={blog?.title || "Blog image"}
//                             className="w-80-px h-80-px object-fit-cover rounded-3"
//                           />
//                         ) : (
//                           <span className="badge bg-neutral-200 text-neutral-600">
//                             No image
//                           </span>
//                         )}
//                       </td>
//                       <td>
//                         {blog?.createdAt
//                           ? formatDate(blog.createdAt)
//                           : "—"}
//                       </td>
//                       <td className="text-end">
//                         <div className="d-flex align-items-center justify-content-end gap-2">
//                           <button
//                             type="button"
//                             className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
//                             onClick={() => handleEdit(blog)}
//                           >
//                             <Icon icon="mingcute:edit-line" className="text-lg" />
//                             Edit
//                           </button>
//                           <button
//                             type="button"
//                             className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
//                             onClick={() => handleDelete(blog?._id || blog?.id)}
//                             disabled={deleteStatus === "loading"}
//                           >
//                             <Icon icon="mingcute:delete-line" className="text-lg" />
//                             Delete
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>
//       <ToastContainer />
//     </div>
//   );
// };

// export default BlogsList;

